/**
 * Estimate wizard — creates a draft (/estimates/new) or edits an existing
 * draft in place (/estimates/:id/edit).
 *
 * Pricing defaults come from `estimator_settings`; each estimate stores its
 * own margin / rate / contingency so later changes to the defaults never
 * rewrite a saved quote. The estimate number is assigned by a database
 * trigger on insert and is shown as "Assigned on save" until then.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { addCalendarDays, calendarDaysBetween, localDateString } from '@pazportal/ui';
import { formatCurrency, formatSolutionType, formatDate } from '../lib/formatters';
import { useCustomersAndLocations } from '../hooks/useCustomersAndLocations';
import { useEstimatorSettings } from '../hooks/useEstimatorSettings';
import { useEstimate } from '../hooks/useEstimate';
import { createEstimate, updateEstimate } from '../lib/estimateWrites';
import { buildProposalText, type ProposalEmailInput } from '../lib/proposalEmail';
import {
  SOLUTION_TYPES,
  isSolutionType,
  type EstimateWriteValues,
  type LineItemWriteValues,
  type SolutionType,
} from '../lib/estimateTypes';

const STEPS: Array<{ label: string; short: string }> = [
  { label: 'Site & client', short: 'Site' },
  { label: 'Solution', short: 'Solution' },
  { label: 'Hardware & labor', short: 'BOM' },
  { label: 'Proposal', short: 'Proposal' },
];

/** Mirrors the solution_type enum; the scope hint names what each line usually covers. */
const SOLUTION_SCOPE: Record<SolutionType, string> = {
  managed_network: 'Switching, routing, firewall, Wi-Fi',
  video_surveillance: 'Cameras, recorders, storage',
  access_control: 'Readers, controllers, door hardware',
  pro_av: 'Displays, audio, conferencing',
  digital_signage: 'Players, screens, content management',
  security_alarm: 'Intrusion and fire panels, monitoring',
  smart_robotics: 'Service robots and integration',
  keytracer: 'Key cabinets and software',
  it_cloud: 'Servers, cloud services, backup',
  pos_systems: 'Terminals, printers, payment devices',
  conferencing: 'Room systems, codecs, scheduling displays',
  audio: 'Speakers, amplifiers, DSP, paging',
};

const FOLLOWUP_OPTIONS = [3, 5, 7, 10, 14];

// Fallbacks used only when estimator_settings has no value for a field.
// They equal the column defaults on `estimates` so a saved row never
// differs from what the database itself would apply.
const FALLBACK_MARGIN_PCT = 22;
const FALLBACK_LABOR_RATE = 125;
const FALLBACK_CONTINGENCY_PCT = 10;
const FALLBACK_VALID_DAYS = 30;
const FALLBACK_FOLLOWUP_DAYS = 5;

interface HwRow { id: number; description: string; qty: number; unitCost: number }
interface LaborRow { id: number; description: string; hours: number }

function toCents(dollars: number): number {
  return Math.round(dollars * 100);
}

function sellCents(unitCostCents: number, marginPct: number): number {
  const divisor = 1 - marginPct / 100;
  if (divisor <= 0) return unitCostCents;
  return Math.round(unitCostCents / divisor);
}

/** valid_until is a DATE: the local calendar day, not the UTC date of the instant. */
function addDaysIso(baseIso: string, days: number): string {
  return addCalendarDays(baseIso, days) ?? localDateString();
}

function parseCcList(raw: string): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((candidate) => {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) emails.push(candidate.toLowerCase());
      else invalid.push(candidate);
    });
  return { emails: Array.from(new Set(emails)), invalid };
}

let rowSeq = 1;
const nextRowId = () => rowSeq++;

/**
 * React Router keeps the same element mounted when only `:id` changes, so
 * the wizard body is keyed by the estimate being edited: moving from one
 * draft's edit URL to another's always rehydrates from that draft, never
 * carrying the previous form (and its "Save changes") across.
 */
export function NewEstimatePage({ mode = 'new' }: { mode?: 'new' | 'edit' }) {
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit' && Boolean(id);
  return <EstimateWizard key={isEdit ? `edit:${id}` : 'new'} isEdit={isEdit} editId={isEdit ? id : undefined} />;
}

function EstimateWizard({ isEdit, editId }: { isEdit: boolean; editId: string | undefined }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Pre-bind the customer when arriving from a Zoho CRM Web Tab
  // (/crm/account/:zohoAccountId resolves the account and redirects here).
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: directory, error: directoryError } = useCustomersAndLocations();
  const { data: defaults, isLoading: defaultsLoading } = useEstimatorSettings();
  const { data: existing, isLoading: existingLoading, error: existingError } = useEstimate(isEdit ? editId : undefined);

  // Step 1
  const [customerId, setCustomerId] = useState(() => (isEdit ? '' : (searchParams.get('customerId') ?? '')));
  const [locationId, setLocationId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [assessmentNotes, setAssessmentNotes] = useState('');

  // Step 2
  const [selectedSolution, setSelectedSolution] = useState<SolutionType | ''>('');

  // Step 3 — per-estimate assumptions (seeded from settings, editable here)
  const [margin, setMargin] = useState(FALLBACK_MARGIN_PCT);
  const [laborRate, setLaborRate] = useState(FALLBACK_LABOR_RATE);
  const [contingency, setContingency] = useState(FALLBACK_CONTINGENCY_PCT);
  const [hwRows, setHwRows] = useState<HwRow[]>([{ id: nextRowId(), description: '', qty: 1, unitCost: 0 }]);
  const [laborRows, setLaborRows] = useState<LaborRow[]>([{ id: nextRowId(), description: '', hours: 0 }]);

  // Step 4
  const [ccRaw, setCcRaw] = useState('');
  const [followupDays, setFollowupDays] = useState(FALLBACK_FOLLOWUP_DAYS);
  const [validDays, setValidDays] = useState(FALLBACK_VALID_DAYS);

  const defaultsApplied = useRef(false);
  const hydrated = useRef(false);

  // Seed assumptions from estimator_settings for a new estimate (once).
  useEffect(() => {
    if (isEdit || defaultsApplied.current || !defaults) return;
    defaultsApplied.current = true;
    if (defaults.hardwareMarginPct !== null) setMargin(defaults.hardwareMarginPct);
    if (defaults.laborRate !== null) setLaborRate(defaults.laborRate);
    if (defaults.contingencyPct !== null) setContingency(defaults.contingencyPct);
    if (defaults.validDays !== null) setValidDays(defaults.validDays);
  }, [defaults, isEdit]);

  // Hydrate the form from the saved estimate when editing (once).
  useEffect(() => {
    if (!isEdit || hydrated.current || !existing) return;
    hydrated.current = true;
    const e = existing.estimate;
    setCustomerId(e.customer_id ?? '');
    setLocationId(e.location_id ?? '');
    setContactName(e.contact_name ?? '');
    setContactEmail(e.contact_email ?? '');
    setContactPhone(e.contact_phone ?? '');
    setAssessmentNotes(e.scope_notes ?? '');
    setSelectedSolution(isSolutionType(e.solution_type) ? e.solution_type : '');
    setMargin(e.hardware_margin_pct ?? FALLBACK_MARGIN_PCT);
    setLaborRate(e.labor_rate_per_hour ?? FALLBACK_LABOR_RATE);
    setContingency(e.contingency_pct ?? FALLBACK_CONTINGENCY_PCT);
    const hw = existing.lineItems
      .filter((i) => i.type === 'hardware')
      .map((i) => ({ id: nextRowId(), description: i.description, qty: i.quantity ?? 1, unitCost: (i.unit_cost_cents ?? 0) / 100 }));
    const labor = existing.lineItems
      .filter((i) => i.type === 'labor')
      .map((i) => ({ id: nextRowId(), description: i.description, hours: i.quantity ?? 0 }));
    setHwRows(hw.length > 0 ? hw : [{ id: nextRowId(), description: '', qty: 1, unitCost: 0 }]);
    setLaborRows(labor.length > 0 ? labor : [{ id: nextRowId(), description: '', hours: 0 }]);
    setCcRaw((e.cc_emails ?? []).join(', '));
    setFollowupDays(e.followup_days ?? FALLBACK_FOLLOWUP_DAYS);
    if (e.valid_until) {
      const days = calendarDaysBetween(e.created_at, e.valid_until) ?? 0;
      setValidDays(days >= 1 ? days : FALLBACK_VALID_DAYS);
    }
  }, [existing, isEdit]);

  const customers = directory?.customers ?? [];
  const locationsForCustomer = useMemo(
    () => (directory?.locations ?? []).filter((l) => l.customer_id === customerId),
    [directory?.locations, customerId],
  );
  const selectedCustomer = customers.find((c) => c.id === customerId);
  const selectedLocation = locationsForCustomer.find((l) => l.id === locationId);

  const handleCustomerChange = (next: string) => {
    setCustomerId(next);
    setLocationId('');
  };

  const handleLocationChange = (next: string) => {
    setLocationId(next);
    const loc = (directory?.locations ?? []).find((l) => l.id === next);
    if (!loc) return;
    // Prefill empty contact fields from the site record; never clobber edits.
    if (!contactName && loc.contact_name) setContactName(loc.contact_name);
    if (!contactEmail && loc.contact_email) setContactEmail(loc.contact_email);
    if (!contactPhone && loc.contact_phone) setContactPhone(loc.contact_phone);
  };

  const updateHw = useCallback((id: number, patch: Partial<HwRow>) => {
    setHwRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);
  const updateLabor = useCallback((id: number, patch: Partial<LaborRow>) => {
    setLaborRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  // Derived money (cents) — recomputed whenever rows or assumptions change.
  const hwComputed = useMemo(
    () =>
      hwRows.map((r) => {
        const unitCostCents = toCents(r.unitCost);
        const unitSellCents = sellCents(unitCostCents, margin);
        return { ...r, unitCostCents, unitSellCents, lineTotalCents: Math.round(unitSellCents * r.qty) };
      }),
    [hwRows, margin],
  );
  const laborRateCents = toCents(laborRate);
  const laborComputed = useMemo(
    () => laborRows.map((r) => ({ ...r, lineTotalCents: Math.round(r.hours * laborRateCents) })),
    [laborRows, laborRateCents],
  );
  const hwSubtotalCents = hwComputed.reduce((s, r) => s + r.lineTotalCents, 0);
  const laborSubtotalCents = laborComputed.reduce((s, r) => s + r.lineTotalCents, 0);
  const subtotalCents = hwSubtotalCents + laborSubtotalCents;
  const contingencyCents = Math.round((subtotalCents * contingency) / 100);
  const totalCents = subtotalCents + contingencyCents;
  const totalLaborHours = laborRows.reduce((s, r) => s + r.hours, 0);

  const cc = useMemo(() => parseCcList(ccRaw), [ccRaw]);
  const validUntil = useMemo(() => {
    const base = isEdit && existing ? existing.estimate.created_at : new Date().toISOString();
    return addDaysIso(base, validDays);
  }, [isEdit, existing, validDays]);

  const previewInput: ProposalEmailInput = {
    estimateNumber: isEdit ? existing?.estimate.estimate_number ?? null : null,
    contactName: contactName || null,
    customerName: selectedCustomer?.name ?? null,
    locationName: selectedLocation?.name ?? null,
    solutionType: selectedSolution || null,
    hardwareCents: hwSubtotalCents,
    laborCents: laborSubtotalCents,
    contingencyCents,
    contingencyPct: contingency,
    totalCents,
    validUntil,
    footer: defaults?.proposalFooter ?? null,
  };
  const preview = buildProposalText(previewInput);

  const saveEstimate = async () => {
    setSaveError(null);
    if (!customerId || !locationId) {
      setSaveError('Select a customer and a location before saving.');
      setStep(0);
      return;
    }
    if (!selectedSolution) {
      setSaveError('Select a solution before saving.');
      setStep(1);
      return;
    }
    if (cc.invalid.length > 0) {
      setSaveError(`These CC entries are not valid email addresses: ${cc.invalid.join(', ')}`);
      setStep(3);
      return;
    }
    setSubmitting(true);
    const values: EstimateWriteValues = {
      customer_id: customerId,
      location_id: locationId,
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      cc_emails: cc.emails,
      solution_type: selectedSolution,
      scope_notes: assessmentNotes.trim() || null,
      hardware_margin_pct: margin,
      labor_rate_per_hour: laborRate,
      contingency_pct: contingency,
      hardware_subtotal_cents: hwSubtotalCents,
      labor_subtotal_cents: laborSubtotalCents,
      contingency_cents: contingencyCents,
      total_cents: totalCents,
      total_labor_hours: totalLaborHours,
      valid_until: validUntil,
      followup_days: followupDays,
    };
    const items: LineItemWriteValues[] = [
      ...hwComputed
        .filter((r) => r.description.trim())
        .map((r, i) => ({
          type: 'hardware' as const,
          description: r.description.trim(),
          quantity: r.qty,
          unit_cost_cents: r.unitCostCents,
          unit_sell_cents: r.unitSellCents,
          sort_order: i,
        })),
      ...laborComputed
        .filter((r) => r.description.trim())
        .map((r, i) => ({
          type: 'labor' as const,
          description: r.description.trim(),
          quantity: r.hours,
          unit_cost_cents: laborRateCents,
          unit_sell_cents: laborRateCents,
          sort_order: hwComputed.length + i,
        })),
    ];

    try {
      const result = isEdit && editId
        ? await updateEstimate(editId, values, items)
        : await createEstimate(values, items);
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate-metrics'] });
      qc.invalidateQueries({ queryKey: ['estimate', result.id] });
      navigate(`/estimates/${result.id}`, {
        state: result.lineItemWarning ? { notice: result.lineItemWarning } : undefined,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'The estimate could not be saved.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit-mode guards ──
  if (isEdit && existingLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading estimate</span>
      </div>
    );
  }
  if (isEdit && (existingError || !existing)) {
    return (
      <div className="p-6 text-center">
        <p className="text-error" role="alert">{existingError instanceof Error ? existingError.message : 'Estimate not found.'}</p>
        <Link to="/dashboard" className="inline-flex min-h-11 items-center mt-2 text-sm text-primary hover:underline">Back to dashboard</Link>
      </div>
    );
  }
  if (isEdit && existing && existing.estimate.status !== 'draft') {
    return (
      <div className="p-6 text-center max-w-md mx-auto">
        <p className="text-sm text-text" role="alert">
          {existing.estimate.estimate_number} is {existing.estimate.status ?? 'not a draft'} and can no longer be edited. Create a new estimate to revise the quote.
        </p>
        <Link to={`/estimates/${existing.estimate.id}`} className="inline-flex min-h-11 items-center mt-2 text-sm text-primary hover:underline">Back to the estimate</Link>
      </div>
    );
  }

  const estimateNumberLabel = isEdit ? existing?.estimate.estimate_number ?? '' : 'Assigned on save';

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-bold text-text">{isEdit ? 'Edit estimate' : 'New estimate'}</h1>
        <p className="text-sm text-text-muted">
          Estimate number: <span className="font-mono text-text">{estimateNumberLabel}</span>
        </p>
      </div>

      {directoryError && (
        <p className="text-sm text-error" role="alert">
          Customers and locations could not be loaded: {directoryError instanceof Error ? directoryError.message : 'request failed'}
        </p>
      )}

      {/* Progress */}
      <ol className="flex items-center gap-1 sm:gap-2" aria-label="Estimate steps">
        {STEPS.map((s, i) => (
          <li key={s.label} className="flex items-center flex-1 min-w-0">
            <button
              type="button"
              onClick={() => i <= step && setStep(i)}
              disabled={i > step}
              aria-current={i === step ? 'step' : undefined}
              aria-label={`Step ${i + 1}: ${s.label}`}
              className={`flex min-h-11 items-center gap-1.5 rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${i > step ? 'cursor-default' : ''}`}
            >
              <span
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  i < step ? 'bg-success text-text-inverse' : i === step ? 'bg-primary text-text-inverse' : 'bg-surface-3 text-text-muted'
                }`}
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span className={`text-[11px] sm:text-xs truncate ${i === step ? 'text-text font-medium' : 'text-text-muted'}`}>
                <span className="sm:hidden">{s.short}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </span>
            </button>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 sm:mx-2 ${i < step ? 'bg-success' : 'bg-surface-3'}`} aria-hidden="true" />}
          </li>
        ))}
      </ol>

      {saveError && (
        <p className="rounded-lg border border-error bg-error-soft px-3 py-2 text-sm text-text" role="alert">{saveError}</p>
      )}

      {/* Step 1 */}
      {step === 0 && (
        <div className="bg-surface rounded-lg border border-border p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-text">Site & client</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField
              label="Customer"
              value={customerId}
              onChange={handleCustomerChange}
              placeholder="Select customer"
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
            />
            <SelectField
              label="Location"
              value={locationId}
              onChange={handleLocationChange}
              disabled={!customerId}
              placeholder={customerId ? (locationsForCustomer.length > 0 ? 'Select location' : 'No locations for this customer') : 'Select a customer first'}
              options={locationsForCustomer.map((l) => ({ value: l.id, label: l.name }))}
            />
            <TextField label="Contact name" value={contactName} onChange={setContactName} autoComplete="name" />
            <TextField label="Contact email" value={contactEmail} onChange={setContactEmail} type="email" autoComplete="email" hint="Proposal recipient." />
            <TextField label="Contact phone" value={contactPhone} onChange={setContactPhone} type="tel" autoComplete="tel" />
          </div>
          <TextAreaField label="Assessment notes" value={assessmentNotes} onChange={setAssessmentNotes} rows={4} />
          <div className="flex justify-end">
            <PrimaryButton onClick={() => setStep(1)} disabled={!customerId || !locationId}>Next: solution</PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 1 && (
        <div className="bg-surface rounded-lg border border-border p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-text">Solution</h2>
          <div role="radiogroup" aria-label="Solution type" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SOLUTION_TYPES.map((type) => {
              const selected = selectedSolution === type;
              return (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSelectedSolution(type)}
                  className={`min-h-11 p-3 sm:p-4 rounded-lg border-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    selected ? 'border-primary bg-primary-soft' : 'border-border hover:bg-surface-2'
                  }`}
                >
                  <p className="text-sm font-medium text-text">{formatSolutionType(type)}</p>
                  <p className="text-[11px] text-text-muted mt-1">{SOLUTION_SCOPE[type]}</p>
                </button>
              );
            })}
          </div>
          <div className="flex justify-between">
            <SecondaryButton onClick={() => setStep(0)}>Back</SecondaryButton>
            <PrimaryButton onClick={() => setStep(2)} disabled={!selectedSolution}>Next: hardware & labor</PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
            <div className="space-y-4 min-w-0">
              {/* Hardware BOM */}
              <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="hw-heading">
                <h3 id="hw-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Hardware BOM</h3>

                {/* Phone: one card per line */}
                <ul className="md:hidden space-y-3" aria-label="Hardware lines">
                  {hwComputed.map((r, idx) => (
                    <li key={r.id} className="rounded-md border border-border bg-surface-2 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-text-muted">Item {idx + 1}</span>
                        <RemoveButton label={`Remove hardware item ${idx + 1}`} onClick={() => setHwRows((rs) => rs.filter((x) => x.id !== r.id))} />
                      </div>
                      <TextField label="Description" value={r.description} onChange={(v) => updateHw(r.id, { description: v })} />
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Qty" value={r.qty} min={0} step={1} onChange={(v) => updateHw(r.id, { qty: v })} />
                        <NumberInput label="Unit cost ($)" value={r.unitCost} min={0} step={0.01} onChange={(v) => updateHw(r.id, { unitCost: v })} />
                      </div>
                      <dl className="grid grid-cols-3 gap-2 text-xs">
                        <div><dt className="text-text-muted">Margin</dt><dd className="text-text">{margin}%</dd></div>
                        <div><dt className="text-text-muted">Unit sell</dt><dd className="text-text">{formatCurrency(r.unitSellCents)}</dd></div>
                        <div><dt className="text-text-muted">Line total</dt><dd className="font-medium text-text">{formatCurrency(r.lineTotalCents)}</dd></div>
                      </dl>
                    </li>
                  ))}
                </ul>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="text-left text-text-muted">
                        <th scope="col" className="pb-2">Description</th>
                        <th scope="col" className="pb-2 w-20">Qty</th>
                        <th scope="col" className="pb-2 w-28">Unit cost ($)</th>
                        <th scope="col" className="pb-2 w-20 text-center">Margin</th>
                        <th scope="col" className="pb-2 w-28 text-right">Unit sell</th>
                        <th scope="col" className="pb-2 w-28 text-right">Line total</th>
                        <th scope="col" className="w-12"><span className="sr-only">Remove</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hwComputed.map((r, idx) => (
                        <tr key={r.id}>
                          <td className="py-1 pr-2"><TextField label={`Item ${idx + 1} description`} hideLabel value={r.description} onChange={(v) => updateHw(r.id, { description: v })} /></td>
                          <td className="py-1 pr-2"><NumberInput label={`Item ${idx + 1} quantity`} hideLabel value={r.qty} min={0} step={1} onChange={(v) => updateHw(r.id, { qty: v })} /></td>
                          <td className="py-1 pr-2"><NumberInput label={`Item ${idx + 1} unit cost`} hideLabel value={r.unitCost} min={0} step={0.01} onChange={(v) => updateHw(r.id, { unitCost: v })} /></td>
                          <td className="py-1 pr-2 text-center text-xs text-text-muted">{margin}%</td>
                          <td className="py-1 pr-2 text-right text-xs text-text whitespace-nowrap">{formatCurrency(r.unitSellCents)}</td>
                          <td className="py-1 pr-2 text-right text-xs font-medium text-text whitespace-nowrap">{formatCurrency(r.lineTotalCents)}</td>
                          <td className="py-1 text-right"><RemoveButton label={`Remove hardware item ${idx + 1}`} onClick={() => setHwRows((rs) => rs.filter((x) => x.id !== r.id))} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => setHwRows((rs) => [...rs, { id: nextRowId(), description: '', qty: 1, unitCost: 0 }])}
                  className="mt-2 inline-flex min-h-11 items-center text-sm text-primary font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                >
                  + Add hardware item
                </button>
              </section>

              {/* Labor */}
              <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="labor-heading">
                <h3 id="labor-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Labor</h3>

                <ul className="md:hidden space-y-3" aria-label="Labor lines">
                  {laborComputed.map((r, idx) => (
                    <li key={r.id} className="rounded-md border border-border bg-surface-2 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-text-muted">Task {idx + 1}</span>
                        <RemoveButton label={`Remove labor task ${idx + 1}`} onClick={() => setLaborRows((rs) => rs.filter((x) => x.id !== r.id))} />
                      </div>
                      <TextField label="Task" value={r.description} onChange={(v) => updateLabor(r.id, { description: v })} />
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput label="Hours" value={r.hours} min={0} step={0.5} onChange={(v) => updateLabor(r.id, { hours: v })} />
                        <div>
                          <p className="text-xs font-medium text-text-muted mb-1">Rate</p>
                          <p className="min-h-11 flex items-center text-sm text-text">{formatCurrency(laborRateCents)}/hr</p>
                        </div>
                      </div>
                      <p className="text-xs"><span className="text-text-muted">Line total </span><span className="font-medium text-text">{formatCurrency(r.lineTotalCents)}</span></p>
                    </li>
                  ))}
                </ul>

                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="text-left text-text-muted">
                        <th scope="col" className="pb-2">Task</th>
                        <th scope="col" className="pb-2 w-24">Hours</th>
                        <th scope="col" className="pb-2 w-28 text-right">Rate</th>
                        <th scope="col" className="pb-2 w-28 text-right">Line total</th>
                        <th scope="col" className="w-12"><span className="sr-only">Remove</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {laborComputed.map((r, idx) => (
                        <tr key={r.id}>
                          <td className="py-1 pr-2"><TextField label={`Task ${idx + 1} description`} hideLabel value={r.description} onChange={(v) => updateLabor(r.id, { description: v })} /></td>
                          <td className="py-1 pr-2"><NumberInput label={`Task ${idx + 1} hours`} hideLabel value={r.hours} min={0} step={0.5} onChange={(v) => updateLabor(r.id, { hours: v })} /></td>
                          <td className="py-1 pr-2 text-right text-xs text-text-muted whitespace-nowrap">{formatCurrency(laborRateCents)}/hr</td>
                          <td className="py-1 pr-2 text-right text-xs font-medium text-text whitespace-nowrap">{formatCurrency(r.lineTotalCents)}</td>
                          <td className="py-1 text-right"><RemoveButton label={`Remove labor task ${idx + 1}`} onClick={() => setLaborRows((rs) => rs.filter((x) => x.id !== r.id))} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => setLaborRows((rs) => [...rs, { id: nextRowId(), description: '', hours: 0 }])}
                  className="mt-2 inline-flex min-h-11 items-center text-sm text-primary font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                >
                  + Add labor task
                </button>
              </section>

              {/* Totals */}
              <div className="bg-surface-2 rounded-lg border border-border p-4 sm:p-5">
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between"><dt className="text-text-muted">Hardware subtotal</dt><dd className="text-text">{formatCurrency(hwSubtotalCents)}</dd></div>
                  <div className="flex justify-between"><dt className="text-text-muted">Labor subtotal</dt><dd className="text-text">{formatCurrency(laborSubtotalCents)}</dd></div>
                  <div className="flex justify-between"><dt className="text-text-muted">Subtotal</dt><dd className="text-text">{formatCurrency(subtotalCents)}</dd></div>
                  <div className="flex justify-between"><dt className="text-text-muted">Contingency ({contingency}%)</dt><dd className="text-text">{formatCurrency(contingencyCents)}</dd></div>
                  <div className="flex justify-between border-t border-border pt-2 mt-2">
                    <dt className="font-bold text-text">Total</dt>
                    <dd className="text-xl font-bold text-text">{formatCurrency(totalCents)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Assumptions */}
            <aside className="bg-surface rounded-lg border border-border p-4 space-y-4 h-fit lg:sticky lg:top-[calc(var(--paz-header-offset)+1rem)]" aria-labelledby="assumptions-heading">
              <div>
                <h3 id="assumptions-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Assumptions</h3>
                <p className="text-[11px] text-text-faint mt-1">
                  {defaultsLoading
                    ? 'Loading saved defaults…'
                    : defaults?.exists
                      ? 'Seeded from Settings; changes here apply to this estimate only.'
                      : 'No saved defaults; values below apply to this estimate only.'}
                </p>
              </div>
              <SliderField label="Hardware margin" value={margin} min={0} max={60} step={0.5} unit="%" onChange={setMargin} />
              <SliderField label="Labor rate" value={laborRate} min={50} max={300} step={1} unit="$/hr" onChange={setLaborRate} />
              <SliderField label="Contingency" value={contingency} min={0} max={30} step={0.5} unit="%" onChange={setContingency} />
            </aside>
          </div>

          <div className="flex justify-between">
            <SecondaryButton onClick={() => setStep(1)}>Back</SecondaryButton>
            <PrimaryButton onClick={() => setStep(3)}>Next: proposal</PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 4 */}
      {step === 3 && (
        <div className="bg-surface rounded-lg border border-border p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-text">Proposal</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="To" value={contactEmail} onChange={setContactEmail} type="email" autoComplete="email" hint="Proposal recipient (contact email)." />
            <TextField
              label="CC"
              value={ccRaw}
              onChange={setCcRaw}
              placeholder="name@example.com, other@example.com"
              hint={cc.invalid.length > 0 ? `Not valid: ${cc.invalid.join(', ')}` : 'Optional. Separate addresses with commas.'}
              invalid={cc.invalid.length > 0}
            />
            <SelectField
              label="Follow-up reminder"
              value={String(followupDays)}
              onChange={(v) => setFollowupDays(Number(v))}
              options={FOLLOWUP_OPTIONS.map((d) => ({ value: String(d), label: `${d} business days after sending` }))}
            />
            <NumberInput label="Valid for (days)" value={validDays} min={1} max={365} step={1} onChange={(v) => setValidDays(Math.max(1, Math.min(365, Math.round(v) || 1)))} hint={`Valid until ${formatDate(validUntil)}.`} />
          </div>

          <div className="border border-border rounded-lg p-4 sm:p-6 bg-surface-2" aria-label="Email preview">
            <p className="text-sm text-text-muted">
              <strong>To:</strong> {contactName || 'Contact'} &lt;{contactEmail || 'no email set'}&gt;
              {cc.emails.length > 0 && (
                <>
                  <br />
                  <strong>CC:</strong> {cc.emails.join(', ')}
                </>
              )}
              <br />
              <strong>Subject:</strong> {preview.subject}
            </p>
            <hr className="my-4 border-border" />
            <p className="text-sm text-text">{preview.greeting}</p>
            <p className="text-sm text-text mt-2">{preview.intro}</p>
            <dl className="mt-4 p-4 bg-surface rounded border border-border text-sm space-y-1">
              {preview.amounts.map((a) => (
                <div key={a.label} className="flex justify-between gap-3"><dt className="text-text-muted">{a.label}</dt><dd className="text-text">{a.value}</dd></div>
              ))}
              <div className="flex justify-between gap-3 border-t border-border pt-2 mt-2"><dt className="font-bold text-text">Total</dt><dd className="text-lg font-bold text-primary">{preview.total}</dd></div>
            </dl>
            {preview.validity && <p className="text-sm text-text mt-4">{preview.validity}</p>}
            <p className="text-sm text-text mt-2">{preview.closing}</p>
            <p className="text-sm text-text mt-2 whitespace-pre-line">{preview.signature}</p>
            {preview.footer && <p className="text-xs text-text-muted mt-4 whitespace-pre-line">{preview.footer}</p>}
          </div>

          <p className="text-xs text-text-muted">
            Saving stores the estimate as a draft. Send the proposal and generate the PDF from the estimate page.
          </p>

          <div className="flex flex-wrap justify-between gap-3">
            <SecondaryButton onClick={() => setStep(2)}>Back</SecondaryButton>
            <PrimaryButton onClick={saveEstimate} disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Save draft'}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Form primitives ────────────────────────────────────────────────────────

const inputClass =
  'w-full min-h-11 px-3 py-2 text-sm border rounded-md bg-surface text-text disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  hideLabel,
  invalid,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  hideLabel?: boolean;
  invalid?: boolean;
  autoComplete?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'block text-xs font-medium text-text-muted mb-1'}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} ${invalid ? 'border-error' : 'border-border'}`}
      />
      {hint && <p id={hintId} className={`text-[11px] mt-1 ${invalid ? 'text-error' : 'text-text-faint'}`}>{hint}</p>}
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  hint,
  hideLabel,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
  hideLabel?: boolean;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'block text-xs font-medium text-text-muted mb-1'}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={Number.isFinite(value) ? value : ''}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => {
          const n = e.target.valueAsNumber;
          onChange(Number.isFinite(n) ? n : 0);
        }}
        className={`${inputClass} border-border`}
      />
      {hint && <p id={hintId} className="text-[11px] text-text-faint mt-1">{hint}</p>}
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-text-muted mb-1">{label}</label>
      <textarea
        id={id}
        value={value}
        rows={rows ?? 4}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-border rounded-md bg-surface text-text resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-text-muted mb-1">{label}</label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} border-border`}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-xs mb-1">
        <label htmlFor={id} className="text-text-muted">{label}</label>
        <span className="inline-flex items-center gap-1">
          <input
            type="number"
            aria-label={`${label} value`}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => {
              const n = e.target.valueAsNumber;
              if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
            }}
            className="w-20 min-h-11 px-2 text-sm text-right border border-border rounded-md bg-surface text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
          <span className="text-text-muted">{unit}</span>
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--primary)]"
      />
    </div>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex h-11 w-11 items-center justify-center rounded-md text-error hover:bg-error-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error"
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );
}

function PrimaryButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-11 items-center px-6 py-2 text-sm font-medium rounded-lg bg-primary text-text-inverse hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-11 items-center px-6 py-2 text-sm rounded-lg border border-border bg-surface text-text hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {children}
    </button>
  );
}
